import { getTimeInMillis } from '../../../utility/datetime'
import { getDefaultValueForField } from './fieldUtil'
import {
  getCommentId,
  getFieldId,
  getLegacySubfieldCommentId,
  getLegacySubfieldId,
  getLegacySubfieldReviewId,
  getReviewId,
  getTableFieldId,
  getTableRowId,
} from './utils'

export const getTableColumns = (fields, subfieldObjects) => {
  const subfieldIds = subfieldObjects.map((it) => it.subfield_template_id)
  const subfields = fields.filter((it) => subfieldIds.includes(it.id))
  return subfields.sort((a, b) => a.sequence - b.sequence)
}

export const getAllRows = (values) => {
  delete values.updatedAt
  const allRows = Object.keys(values)?.map((it) => Number(it?.split('_')[1]))
  return Array.from(new Set(allRows))
}

export const convertFromTableFieldDataToLegacy = (tableFieldData) => {
  let convertedData = {}
  for (const [rowId, rowValue] of Object.entries(tableFieldData)) {
    const row = rowId.split('_')[1]
    for (const [key, value] of Object.entries(rowValue)) {
      convertedData = { ...convertedData, [`${key}__${row}`]: value }
    }
  }
  return convertedData
}
export const convertFromTableReviewDataToLegacy = (tableFieldData) => {
  let convertedData = {}
  for (const [rowId, rowValue] of Object.entries(tableFieldData || {})) {
    const row = rowId.split('_')[1]
    for (const [key, value] of Object.entries(rowValue)) {
      convertedData = {
        ...convertedData,
        [`r_${key.split('_')[1]}__${row}`]: value,
      }
    }
  }
  return convertedData
}
export const convertFromTableCommentDataToLegacy = (tableFieldData) => {
  let convertedData = {}
  for (const [rowId, rowValue] of Object.entries(tableFieldData || {})) {
    const row = rowId.split('_')[1]
    for (const [key, value] of Object.entries(rowValue)) {
      convertedData = {
        ...convertedData,
        [`c_${key.split('_')[1]}__${row}`]: value,
      }
    }
  }
  return convertedData
}

const getAllColumnsRows = (allValues, columns) => {
  const allRows = [];

  for (const column of columns) {
    const derivedIds = column.is_derived_field && column.derived_from_field_template_id
      ? String(column.derived_from_field_template_id).split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
      : [];

    const matchingKeys = Object.keys(allValues).filter((key) => {
      const [fieldPart] = key.split("__");
      const [, fieldIdStr] = fieldPart.split("_") || [];
      const fieldId = parseInt(fieldIdStr);
      if (isNaN(fieldId)) return false;
      return fieldId === column.id || derivedIds.includes(fieldId);
    });

    const rows = matchingKeys
      .map((key) => {
        const [, rowIndex] = key.split("__");
        return Number(rowIndex);
      })
      .filter((n) => !isNaN(n));

    allRows.push(...rows);
  }

  return Array.from(new Set(allRows)).sort((a, b) => a - b);
};

const getLatestRow = (allValues, columns) => {
  const allRows = getAllColumnsRows(allValues, columns);
  return allRows.length ? Math.max(...allRows) : null;
};


const getColumnKey = (column, row) => {
  return getLegacySubfieldId(column?.id, row)
}

export const convertFromLegacyToRowWiseData = (
  tableFieldId,
  allValues,
  columns = []
) => {
  const latestActiveRow = allValues[getFieldId(tableFieldId)] || 1;
  const calculatedLatestRow = getLatestRow(allValues, columns);

  let rowsData = {};
  const maxRow = Math.max(latestActiveRow, calculatedLatestRow);

  for (let row = 1; row <= maxRow; row++) {
    let columnsData = { updatedAt: getTimeInMillis() };

    const shouldInsertRow = columns.some(
      (field) => allValues[getColumnKey(field, row)] !== undefined
    );

    if (shouldInsertRow) {
      columns.forEach((field) => {
        columnsData = {
          ...columnsData,
          [getFieldId(field.id)]: allValues[getColumnKey(field, row)],
        };
      });
      rowsData = { ...rowsData, [getTableRowId(row)]: columnsData };
    }
  }

  return { [getTableFieldId(tableFieldId)]: rowsData };
};


const getLatestRowForReview = (allValues, columns) => {
  const allKeys = Object.keys(allValues).filter((it) => {
    const mm = it.split('__')[0]
    if (mm) {
      return columns[0].id
    }
  })

  const allRows = allKeys.map((it) => +it.split('__')[1]).filter((it) => !isNaN(it))

  return Math.max(...Array.from(new Set(allRows)))
}

export const convertFromLegacyReviewToRowWiseReview = (
  tableFieldId, // it's need to be field.id only
  latestActiveRow,
  allValues,
  columns = []
) => {
  const calculatedLatestRow = getLatestRowForReview(allValues, columns);
  const greatestRow = Math.max(latestActiveRow, calculatedLatestRow);

  let rowsData = {};
  for (let row = 1; row <= greatestRow; row++) {
    let columnsData = {};

    // check across all columns
    const shouldInsertRow = columns.some(
      (field) => getLegacySubfieldReviewId(field.id, row) in allValues
    );

    if (shouldInsertRow) {
      columns.forEach((field) => {
        if (getLegacySubfieldReviewId(field.id, row) in allValues) {
          columnsData = {
            ...columnsData,
            [getReviewId(field.id)]: allValues[getLegacySubfieldReviewId(field.id, row)],
          };
        }
      });

      rowsData = {
        ...rowsData,
        [getTableRowId(row)]: { ...columnsData, updatedAt: getTimeInMillis() },
      };
    }
  }
  return { [getTableFieldId(tableFieldId)]: rowsData };
};

export const convertFromLegacyReviewToRowWiseComment = (
  tableFieldId, // it's need to be field.id only
  latestActiveRow,
  allValues,
  columns = []
) => {
  let rowsData = {};
  for (let row = 1; row <= latestActiveRow; row++) {
    let columnsData = {};

    // check across all columns
    const shouldInsertRow = columns.some(
      (field) => allValues[getLegacySubfieldCommentId(field.id, row)] !== undefined
    );

    if (shouldInsertRow) {
      columns.forEach((field) => {
        columnsData = {
          ...columnsData,
          [getCommentId(field.id)]: allValues[getLegacySubfieldCommentId(field.id, row)],
        };
      });

      rowsData = {
        ...rowsData,
        [getTableRowId(row)]: { ...columnsData, updatedAt: getTimeInMillis() },
      };
    }
  }
  return { [getTableFieldId(tableFieldId)]: rowsData };
};


export const getTableRowWithDefaultValues = (columns = []) => {
  let columnsData = {}
  columns.forEach((column) => {
    columnsData = {
      ...columnsData,
      [getFieldId(column.id)]: getDefaultValueForField(column),
    }
  })
  return { [`tr_1`]: columnsData }
}

export const getTableRowFromLegacyAllValues = (allValues, columns) => {
  const firstColumnRows = [] // columns[0]
  const secondColumnRows = [] // columns[1]

  Object.keys(allValues).forEach((it) => {
    const fieldKey = it.split('__')[0]
    if (fieldKey === getFieldId(columns[0].id)) {
      firstColumnRows.push(Number(it.split('__')[1]))
    } else if (fieldKey === getFieldId(columns[1].id)) {
      secondColumnRows.push(Number(it.split('__')[1]))
    }
  })

  return secondColumnRows
}
